import React, { useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, Outlet } from 'react-router-dom';
import Login from './components/Auth/Login';
import Dashboard from './components/Dashboard/Dashboard';
import ManageEmployee from './components/Employee/ManageEmployee';
import Header from './components/common/Header';
import Sidebar from './components/common/Sidebar';
import Footer from './components/common/Footer';
import Profile from './components/Profile/Profile';
import ResetPassword from './components/Auth/ResetPassword';
import EmployeeCheckDetails from './components/Employee/EmployeeCheckDetails';
import EmployeeChecks from './components/Employee/EmployeeChecks';
import EmployeeChecksSummary from './components/Employee/EmployeeChecksSummary';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import Unauthorized from './components/Auth/Unauthorized';
import ApplicationForm from './components/Application/ApplicationForm'; // Import the new component
import ApplicationAction from './components/Application/ApplicationAction'; // Import the new component
import { ApplicationList } from './components/Application/ApplicationList';
import ProtectedEmployeePacket from './components/Auth/ProtectedI9Form';
import EmployeePacket from './components/Application/EmployeePacket';
import EmailTokenValidator from './components/Auth/EmailTokenValidator';
import ThankYou from './components/Application/ThankYou'; // Import the ThankYou component
import EmployeePacketThankYou from './components/Application/EmployeePacketThankYou'; // Import Employee Packet ThankYou component
import { AuthProvider, useAuth } from './context/AuthContext';

import './App.css';

// Loading component to show while checking authentication
const LoadingScreen: React.FC = () => (
  <div className="loading-screen" style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    flexDirection: 'column'
  }}>
    <div className="spinner-border" role="status">
      <span className="sr-only">Loading...</span>
    </div>
    <p className="mt-3">Checking authentication...</p>
  </div>
);

// Main app content component
const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  // Show loading screen while checking authentication
  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <Routes>
      <Route path="/login" element={
        isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />
      } />
      <Route path="/unauthorized" element={<Unauthorized />} />
      <Route path="/apply" element={<ApplicationForm />} /> {/* New public route for job application */}
      <Route path="/thank-you" element={<ThankYou />} /> {/* Thank you page after application submission */}
      <Route path="/employee-packet-thank-you" element={<EmployeePacketThankYou />} /> {/* Thank you page after employee packet submission */}
      <Route path="/application-action" element={<ApplicationAction />} />
      <Route path="/employee-packet" element={<ProtectedEmployeePacket />} />
      <Route path="/validate-token" element={<EmailTokenValidator />} /> {/* New route for email token validation */}
      <Route element={<MainLayout />}>
        <Route path="/" element={
          isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
        } />
        <Route path="/dashboard" element={<ProtectedRoute roles={['Employee']}  element={<Dashboard />} />} />
        <Route path="/manage-employee" element={<ProtectedRoute roles={['Admin','HR-Manager']} element={<ManageEmployee />} />} />
        <Route path="/profile" element={<ProtectedRoute roles={['Employee']} element={<Profile />} />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/employee-check-details" element={<ProtectedRoute roles={['Employee']} element={<EmployeeCheckDetails />} />} />
        <Route path="/employee-checks" element={<ProtectedRoute roles={['Employee']} element={<EmployeeChecks />} />} />
        <Route path="/employee-checks-summary" element={<ProtectedRoute roles={['Admin']} element={<EmployeeChecksSummary />} />} />
        <Route path="/applications" element={<ProtectedRoute roles={['Admin', 'HR-Manager', 'CFO']} element={<ApplicationList />} />} />
        <Route path="/staff-employee-packet" element={<ProtectedRoute roles={['Admin', 'HR-Manager', 'CFO']} element={<EmployeePacket />} />} />
      </Route>
    </Routes>
  );
};

const MainLayout: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  
  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  const closeSidebar = () => setIsSidebarOpen(false);
  
  return (
    <div className={`App ${isSidebarOpen ? 'sidebar-shrink' : ''}`}>
      <Header onToggleSidebar={toggleSidebar} />
      <Sidebar isOpen={isSidebarOpen} onClose={closeSidebar} />
      <div className="main-container">
        <Outlet />
      </div>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App;

